/** Decorative unsubscribe footer for email preview. The actual footer in sent
 * emails is handled by Mailgun's native unsubscribe tracking. */
export const UNSUBSCRIBE_FOOTER_HTML = `<div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;font-size:12px;color:#6b7280"><p style="margin:0"><a href="#" style="color:#6b7280;text-decoration:underline">Unsubscribe from marketing emails</a></p></div>`;

const COMPOSE_PREVIEW_STYLES =
  "*{word-break:break-word;overflow-wrap:break-word}body{margin:0;padding:16px;font-family:sans-serif;font-size:13px}";

/** Placeholder shown in compose preview when body is empty */
export const COMPOSE_PREVIEW_PLACEHOLDER =
  "<p style='color:#888;font-size:13px;margin:0'>Start typing to see preview...</p>";

// ---------------------------------------------------------------------------
// Shared preview builder
// ---------------------------------------------------------------------------

export interface EmailPreviewSrcDocOptions {
  /** The HTML body content */
  html: string;
  /** Append the marketing unsubscribe footer */
  showFooter?: boolean;
  /** Use compose-style base styles (word-break, padding, font) */
  useComposeStyles?: boolean;
  /** Placeholder HTML when html is empty (compose preview only) */
  emptyPlaceholder?: string;
}

/**
 * Builds the srcDoc string for a sandboxed email preview iframe.
 *
 * The HTML goes in exactly as it will go out. It used to be sanitised and
 * filtered here first — DOMPurify plus a pass that dropped the CSS properties
 * Gmail and Outlook ignore — which meant the preview was a simulation of the
 * email rather than the email, and the two could differ without anyone
 * noticing. Foreign markup is now made safe once, on the way into the document
 * (`blocks/html-fallback/sanitize.ts`), and everything sent is generated from
 * that document. The iframe is sandboxed without `allow-scripts`, so nothing
 * here executes regardless.
 */
export function buildEmailPreviewSrcDoc(
  options: EmailPreviewSrcDocOptions,
): string {
  const {
    html,
    showFooter,
    useComposeStyles,
    emptyPlaceholder = COMPOSE_PREVIEW_PLACEHOLDER,
  } = options;

  const content = html.trim() ? html : emptyPlaceholder;
  const styles = useComposeStyles
    ? `<style>${COMPOSE_PREVIEW_STYLES}</style>`
    : "";
  const footer = showFooter ? UNSUBSCRIBE_FOOTER_HTML : "";

  return `${styles}${content}${footer}`;
}
